import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser'

// widgets
import NewsletterWidget from '../micro-frontends/DuplicateNewsLetterWidget';
import ContactSalesForm from '../widgets/ContactSalesForm';
import PromotedReportWidget from '../widgets/PromotedReport';

// for startup/investor hover component
import { hoverHelpers } from 'shared-helpers';

// constants
import { WEB_URL } from '../utilities/endpoints'
import { MAGNITT_ACCESS_ROLES } from '../utilities/constants/userRoles';

// helpers
import AmplitudeHelperClass from '/src/utilities/amplitudeHelper';



const amplitudeHelper = new AmplitudeHelperClass();

const ContentBody = React.memo(props => {

    // fix for test articles without body
    let body = (props.data.body ? props.data.body : "");

    // news article category utm link
    const utmLink = props.data && props.data.utm_link ? props.data.utm_link : "";

    // setting inital body from Server
    const [contentBody, setContentBody] = useState(parse(body))
    const htmlAdded = useRef(null)

    const mobileWidgetClassName = "mobile-widget hidden-xl-up";

    const addWidgets = () => {
        const contact_widgets = document.querySelectorAll("[id='widget-contact-sales']");
        if (contact_widgets) {
            for (var i = 0; i < contact_widgets.length; i++) {
                // get the form type
                let formType = contact_widgets[i].getAttribute('data-form-type');
                if (!formType) {
                    formType = "enterprise";
                }

                const widget = <ContactSalesForm formType={formType} pageType={props.type} />;

                // desktop => widget displayed in sidebar
                // we have to add the widget to a new div instead of adding it directly to the sidebar. this is because ReactDOM.render replaces other elements in the div
                const widgetSidebar = document.getElementById('custom-widgets-container');
                if (widgetSidebar) {
                    let div = document.createElement('div');
                    div.classList.add('contact-widget-box');
                    widgetSidebar.appendChild(div);
                    ReactDOM.render(
                        widget,
                        document.querySelector('.contact-widget-box')
                    );
                }
                
                // mobiles => widget displayed at position specified in article text
                ReactDOM.render(
                    <div className={mobileWidgetClassName}>
                        {widget}
                    </div>,
                    contact_widgets[i]
                );
            }
        }

        addNewsletterWidget();

        /*
         * Promoted Report widget
         * a custom widget used to promote a report on a news / research single page
         * 
         * example usage: <div id="widget-research-actions" data-report="reportId" data-promo-title="Promo section title" data-report-promo="Sample text promoting the report or linking it to the article content"></div>
         */
        const elements = document.querySelectorAll(`[id='widget-research-actions'`);
        if (elements) {
            for (let i = 0; i < elements.length; i++) {

                let element    = elements[i];
                let reportId   = element.getAttribute('data-report'); // get report id from string added by marketing
                let promoTitle = element.getAttribute('data-promo-title');
                let promoText  = element.getAttribute('data-report-promo');

                if (reportId) {
                    ReactDOM.render(
                        <PromotedReportWidget reportId={reportId} baseLanguage={props.data.lang} promoText={promoText} promoTitle={promoTitle} />,
                        element
                    );
                }
                
            }
        }
    }

    const addHoverWidget = () => {

        const tagNameSelector = /^widget-startup-hover/;
        const hoverableElements = [], els = document.getElementsByTagName('*');
        const user = props.user;
        let userHasDirectoryAccess = false;
        if (user && user.roles && user.roles.length > 0) {
            userHasDirectoryAccess = (user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access) || user.roles.includes(MAGNITT_ACCESS_ROLES.limited_directory_access))
        }

        for (let i = 0; i < els.length; i++) {
            if (tagNameSelector.test(els[i].id)) {
                hoverableElements.push(els[i]);
            }
        }

        hoverableElements.map((element) => {

            const data = {
                id: element.getAttribute("data-id"),
                type: element.getAttribute("data-type").toLowerCase(),
                name: element.textContent.trim(),
                code: element.textContent.trim().toLowerCase().replace(/ /g, '')
            }

            let URL = `${WEB_URL}/${data.type}s/${data.code}-${data.id}`;

            if (utmLink !== "") {
                URL = URL.concat(`?${utmLink}`);
            }

            let newContent =
                <a target="_blank"
                    rel="noopener noreferrer"
                    href={URL}
                    onMouseEnter={(event) => hoverHelpers.handleEntityHover(data, event, userHasDirectoryAccess)}
                    onMouseLeave={() => hoverHelpers.handleMouseOut()}>
                    {data.name}
                </a>
            ReactDOM.render(newContent, element);

        })

    }

    useEffect(() => {

        let { data, type } = props;

        if (!htmlAdded.current) {

            let bodyContent = [];

            if (data.body) {

                // clean the string
                const regex = [
                    /(\r\n|\n|\r)/gm,   // for line breaks
                    /font-size:[^;]+/g, // for auto added font size
                ]
                const replace = [
                    '<p></p>',
                    '<p>&nbsp;</p>',
                    '<p dir="ltr">&nbsp;</p>',
                    '<p dir="rtl">&nbsp;</p>',
                    '<div>	&nbsp;</div>',
                    '\u003C\u002Fp\u003E', // unicode representation of '<p></p>'
                    '<strong>&nbsp;</strong>',
                    '<p dir="RTL">&nbsp;</p>'
                ];

                for (let j in regex) {
                    data.body = data.body.replaceAll(regex[j], "");
                }
                for (let i in replace) {
                    while (data.body.includes(replace[i])) {
                        data.body = data.body.replaceAll(replace[i], "");
                    }
                }
            }

            if (type === "news") {
                if (data.body) {
                    let delimiter = '</p>';
                    let splitBody = data.body.split(delimiter);

                    // check minimum paragraphs and hide widgets accordingly
                    // only affects startup and investor widgets (news)
                    const minParas = 2;
                    let displayWidgets = false;
                    if (splitBody.length > minParas) {
                        displayWidgets = true;
                    }

                    // detect which elements will overlap
                    let contentBodyObj = [];
                    splitBody.map((x, index) => {
                        let overlapsWidget = x.startsWith("<p") ? false : true;
                        let overlapsImages = x.includes("<img") ? true : false;
                        let overlaps = (overlapsWidget || overlapsImages);
                        let temp = { content: x, overlaps, index };
                        contentBodyObj.push(temp);
                    });

                    for (let i = 0; i < splitBody.length; i++) {
                        bodyContent += splitBody[i] + delimiter;
                    }
                    data.body = bodyContent;
                }
            }

            // TODO
            // Arabic sentences in English articles have a different font due to missing tag lang="ar"
            // English sentences in Arabic articles get right aligned

            const text = purifyTextContent(data.body);
            const body = <div dangerouslySetInnerHTML={{ __html: text }} key={'htmlbody1'} className="mt-0" />;
            setContentBody(body);
            htmlAdded.current = true;

        } else {

            addHoverWidget();
            addWidgets();

        }

        // add analytics tracking to track when form popup is triggered
        if (!window.modalClickListenerAdded) {
            const contentSection = document.getElementsByClassName('content-column-main')[0];
            if (contentSection) {
                contentSection.addEventListener('click', function (event) {
                    let eventTarget = null;
                    if (event.target.matches('[data-toggle="modal"]') && event.target.matches('[data-target="#contactFormModal"]')) {
                        eventTarget = event.target;
                    } else if (event.target.nodeName === "IMG") {
                        eventTarget = event.target.closest('[data-target="#contactFormModal"]');
                    }

                    if (eventTarget) {
                        const triggerSource = eventTarget.getAttribute('data-trigger-source');
                        trackFormTrigger(triggerSource);
                    }
                });
                window.modalClickListenerAdded = true;
            }
        }

        // Locate all <a> tags in the document and ensure they have an aria-label attribute for accessibility.
        // If an aria-label is missing, add one to improve screen reader support.
        // This is necessary because the content is dynamically added from the admin panel.
        const aTags = document?.querySelectorAll('a');
        aTags.forEach((aTag) => {
            // Check if the <a> tag has a child <img> element
            const hasImageChild = aTag.querySelector('img') !== null;

            if (!aTag.hasAttribute('aria-label') && hasImageChild) {
                aTag.setAttribute('aria-label', 'Open contact form modal triggered by body text');
            }
        });

    }, [htmlAdded.current])

    const trackFormTrigger = (triggerSource) => {
        const { user, data } = props;
        const subbedUser  = (data.download_policy && data.download_policy.user_sub_date !== null);

        let userType = null;
        if (user.is_loggedin) {
            userType = (subbedUser ? 'Subscribed User' : 'Free User');
        } else {
            userType = 'Logged Out User';
        }

        amplitudeHelper.trackEvent('Clicked Access Report', {
            report_name: data.name,
            user_type: userType,
            clicked_from: triggerSource
        });
    }

    // purify text content
    const purifyTextContent = (body) => {
        // enable marketing team to use target="_blank" (open link in new tab)
        DOMPurify.addHook('afterSanitizeAttributes', function (node) {
            if ('target' in node && node instanceof HTMLElement) {
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
            }

            // add form trigger source info for analytics tracking
            if (node.getAttribute('data-target') == '#contactFormModal') {
                const isImageLink = (node.getElementsByTagName('img').length > 0);
                if (isImageLink) {
                    node.setAttribute('data-trigger-source', 'Body Image');
                } else {
                    node.setAttribute('data-trigger-source', 'Body Text');
                }
            }
        });

        const text = DOMPurify.sanitize(body, 
            { 
                ADD_TAGS: ["iframe"], 
                ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'] 
            }
        );
        return text;
    }

    const addNewsletterWidget = () => {
        // desktop => widget displayed by default in the sidebar
        // mobiles => widget displayed at position specified in article text
        if (document.getElementById('widget-newsletter')) {
            ReactDOM.render(
                <div className={mobileWidgetClassName}>
                    <NewsletterWidget
                        history={props.history}
                        data={{ pageType: (props.type === "news") ? "singleNews" : "singleResearch" }}
                    />
                </div>,
                document.getElementById('widget-newsletter')
            );
        }
    }

    return <>{contentBody}</>

})

export default ContentBody;
